import { defineStore } from 'pinia'
import { useAlertsStore } from '@/store/pinia/alerts'
import { useOrganizationStore } from '@/store/pinia/organization'
import {
  makeClearErrorsAction,
  makeResetStoreAction,
  makeSetErrorsAction,
} from '@/utils/helpers/pinia'
import i18n from '@/utils/plugins/i18n'

/**
 * Per-organization OIDC group mapping + reconciliation settings.
 *
 * Mapping rows are `(group_name -> team)`; multiple teams for the same group
 * are expressed as multiple rows (no per-mapping role — the org-level role is
 * driven by `settings.default_role_canonical`).
 *
 * @see https://docs.staging.cycloid.io/api/index.html#tag/Organization-OIDC-Group-Mappings
 */
export const state = () => ({
  /** @type {Array<Object>} */
  mappings: [],
  /** @type {Object|null} */
  settings: null,
  errors: {
    mappings: [],
    settings: [],
  },
  fetchInProgress: {
    mappings: false,
    settings: false,
  },
})

export const useOidcMappingsStore = defineStore('oidcMappings', {
  state,

  getters: {
    /**
     * Whether the org is in OIDC strict mode. When true, local member/team/
     * invite edits must be hidden (the backend also denies them at the API).
     * Defaults to false until settings are fetched.
     * @returns {Boolean}
     */
    isOidcManaged: (state) => state.settings?.oidc_managed ?? false,
  },

  actions: {
    /**
     * Fetch the OIDC group mappings configured for the current organization.
     * @returns {Promise<void>}
     */
    async FETCH_MAPPINGS () {
      const organizationStore = useOrganizationStore()
      this.CLEAR_ERRORS('mappings')
      this.fetchInProgress.mappings = true

      const { data, errors }
        = (await this.$cycloid.ydAPI.listOIDCGroupMappings(
          organizationStore.orgCanonical,
        )) || {}

      this.fetchInProgress.mappings = false

      if (errors) {
        this.SET_ERRORS({ errors, key: 'mappings' })
        return
      }

      this.mappings = data ?? []
    },

    /**
     * Create a new OIDC group mapping `(group_name -> team)`.
     * @param {Object} payload
     * @param {String} payload.groupName    The OIDC group claim value to match.
     * @param {String} payload.teamCanonical The team to assign the user to.
     * @returns {Promise<Object|undefined>}
     */
    async CREATE_MAPPING ({ groupName, teamCanonical }) {
      const organizationStore = useOrganizationStore()
      const alertsStore = useAlertsStore()
      this.CLEAR_ERRORS('mappings')

      const body = { group_name: groupName, team_canonical: teamCanonical }
      const { data, errors }
        = (await this.$cycloid.ydAPI.createOIDCGroupMapping(
          organizationStore.orgCanonical,
          body,
        )) || {}

      if (errors) {
        this.SET_ERRORS({ errors, key: 'mappings' })
        alertsStore.SHOW_ALERT({ type: 'error', content: errors })
        return
      }

      if (data) {
        await this.FETCH_MAPPINGS()
        alertsStore.SHOW_ALERT({
          type: 'success',
          content: i18n.global.t('alerts.success.oidcMappings.created', {
            groupName,
          }),
        })
        return data
      }
    },

    /**
     * Delete an OIDC group mapping by id.
     * @param {Object} payload
     * @param {Number|String} payload.id The mapping id.
     * @returns {Promise<void>}
     */
    async DELETE_MAPPING ({ id }) {
      const organizationStore = useOrganizationStore()
      const alertsStore = useAlertsStore()
      this.CLEAR_ERRORS('mappings')

      const { errors }
        = (await this.$cycloid.ydAPI.deleteOIDCGroupMapping(
          organizationStore.orgCanonical,
          id,
        )) || {}

      if (errors) {
        this.SET_ERRORS({ errors, key: 'mappings' })
        alertsStore.SHOW_ALERT({ type: 'error', content: errors })
        return
      }

      await this.FETCH_MAPPINGS()
      alertsStore.SHOW_ALERT({
        type: 'success',
        content: i18n.global.t('alerts.success.oidcMappings.deleted'),
      })
    },

    /**
     * Fetch the per-organization OIDC reconciliation settings.
     * @returns {Promise<void>}
     */
    async FETCH_SETTINGS () {
      const organizationStore = useOrganizationStore()
      this.CLEAR_ERRORS('settings')
      this.fetchInProgress.settings = true

      const { data, errors }
        = (await this.$cycloid.ydAPI.getOIDCOrganizationSettings(
          organizationStore.orgCanonical,
        )) || {}

      this.fetchInProgress.settings = false

      // A 404 here means the org has no OIDC settings configured yet, which is
      // the normal state before strict mode / mappings are set up. Treat it as
      // defaults so the mapping pane still renders (the feature is accessible).
      if (errors && errors[0]?.code !== 'NotFound') {
        this.SET_ERRORS({ errors, key: 'settings' })
        return
      }

      this.settings = data ?? { default_role_canonical: '', oidc_managed: false, oidc_no_match_policy: 'keep_membership', oidc_configured_in_hierarchy: false }
    },

    /**
     * Create or update the per-organization OIDC settings.
     * The backend rejects `oidc_no_match_policy = 'eject'` when
     * `oidc_managed` is false with HTTP 422.
     * @param {Object} payload
     * @param {String} [payload.defaultRoleCanonical] Org-level role on provisioning. Empty clears it.
     * @param {Boolean} payload.oidcManaged           Strict-mode flag.
     * @param {String} payload.oidcNoMatchPolicy      'keep_membership' | 'eject'.
     * @returns {Promise<Object|undefined>}
     */
    async UPDATE_SETTINGS ({
      defaultRoleCanonical,
      oidcManaged,
      oidcNoMatchPolicy,
    }) {
      const organizationStore = useOrganizationStore()
      const alertsStore = useAlertsStore()
      this.CLEAR_ERRORS('settings')

      const body = {
        oidc_managed: oidcManaged,
        oidc_no_match_policy: oidcNoMatchPolicy,
        ...(_.isEmpty(defaultRoleCanonical)
          ? {}
          : { default_role_canonical: defaultRoleCanonical }),
      }

      const { data, errors }
        = (await this.$cycloid.ydAPI.updateOIDCOrganizationSettings(
          organizationStore.orgCanonical,
          body,
        )) || {}

      if (errors) {
        this.SET_ERRORS({ errors, key: 'settings' })
        alertsStore.SHOW_ALERT({ type: 'error', content: errors })
        return
      }

      if (data) {
        this.settings = data
        alertsStore.SHOW_ALERT({
          type: 'success',
          content: i18n.global.t('alerts.success.oidcMappings.settingsUpdated'),
        })
        return data
      }
    },

    /**
     * Clear errors for a given key (or all errors when no key is passed).
     * @returns {void}
     */
    CLEAR_ERRORS: makeClearErrorsAction(state),

    /**
     * Reset store state back to its initial values.
     * @returns {void}
     */
    RESET_OIDC_MAPPINGS_STATE: makeResetStoreAction(state),

    /**
     * Replace the errors array for a given key.
     * @returns {void}
     */
    SET_ERRORS: makeSetErrorsAction(),
  },
})
